<template>
  <HXContentCard title="Honorarios de Éxito">
    <div class="mt-2 p-lg-4">
      <!-- cargar honorarios -->
      <b-modal
        id="cargar-honorarios"
        title="Cargar Honorarios"
        size="md"
        hide-footer
      >
        <!-- descargar plantilla de ejemplo -->
        <div class="d-flex justify-content-end align-items-center">
          <div
            class="mr-1 bg-dark rounded-circle d-flex justify-content-center align-items-center"
            style="width: 30px; height: 30px"
            id="info"
          >
            <i class="fa-solid fa-exclamation"></i>
          </div>
          <b-popover target="info" triggers="hover" placement="top">
            <template #title>Importante!</template>
            <p class="font-weight-bold">
              No modifiques los encabezados ni los elimines.
            </p>
            <p>
              Por favor, asegúrate de seguir estas indicaciones antes de cargar
              el archivo.
            </p>
          </b-popover>
          <vs-button success icon flat :href="urlPlantila">
            <i class="fas fa-file-excel mr-2"></i>
            Descargar Plantilla
          </vs-button>
        </div>
        <div class="row mb-3">
          <div class="col-6">
            <label class="form-label">Fecha Inicio</label>
            <input type="date" class="form-control" v-model="fechaInicio" />
          </div>
          <div class="col-6">
            <label class="form-label">Fecha Fin</label>
            <input type="date" class="form-control" v-model="fechaFin" />
          </div>
        </div>
        <!-- drop file -->
        <DropFileBasic
          @handleFileData="handleFileData"
          @fileRemoved="handleFileRemoved"
          :loader="loader"
          :fileTypes="[
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          ]"
        />

        <!-- file validation -->
        <p v-if="messageFileValidation" class="py-3">
          <i class="fas fa-times text-danger"></i>
          {{ messageFileValidation }}
        </p>
        <div class="d-flex justify-content-end mt-2">
          <vs-button
            primary
            icon
            @click="sendDataFile"
            :disabled="!sendData || !fechaInicio || !fechaFin"
          >
            <i class="fas fa-upload mr-1"></i>
            Subir y Guardar
          </vs-button>
        </div>
      </b-modal>

      <div v-if="isAdmin" class="d-flex justify-content-end">
        <vs-button success icon v-b-modal="'cargar-honorarios'">
          <i class="fas fa-file-import mr-1"></i>
          Cargar Honorarios</vs-button
        >
      </div>

      <div class="row">
        <div class="col-12 col-lg-4 px-5">
          <label for="filterDate" class="form-label"
            ><i class="fas fa-calendar-days"></i> Filtrar por fecha:</label
          >
          <input
            id="date"
            type="month"
            class="form-control"
            v-model="currentFilterDate"
            @input="handleFilterDateInput"
          />
        </div>
        <div v-if="liquidacionesDisponibles.length > 1" class="col-12 col-lg-4 px-5">
          <label class="form-label"
            ><i class="fas fa-list"></i> Seleccionar liquidación:</label
          >
          <select
            class="form-control"
            v-model="liquidacionSeleccionada"
            @change="mostrarLiquidacion(liquidacionSeleccionada)"
          >
            <option
              v-for="liq in liquidacionesDisponibles"
              :key="liq.id"
              :value="liq"
            >
              {{ formatearFecha(liq.fechaInicio) }} -
              {{ formatearFecha(liq.fechaFin) }}
            </option>
          </select>
          <small v-if="liquidacionSeleccionada && liquidacionSeleccionada.fechaActualizacion" class="text-muted mt-1 d-block">
            <i class="fas fa-clock mr-1"></i>
            Última actualización: {{ formatearFechaHora(liquidacionSeleccionada.fechaActualizacion) }}
          </small>
        </div>
      </div>

      <div v-if="excelData.length > 0" class="px-4 mt-3">
        <vs-table striped>
          <template #notFound>
            <span>No se encontraron resultados</span>
          </template>
          <template #header>
            <vs-input v-model="search" border placeholder="Buscar..." />
          </template>
          <template #thead>
            <vs-tr>
              <vs-th
                v-for="header in currentHeaders"
                :key="header"
                :colspan="encabezadosCombinados(header)"
                sort
                @click="excelData = $vs.sortData($event, excelData, header)"
              >
                {{ header }}
              </vs-th>
            </vs-tr>
          </template>
          <template #tbody>
            <!-- Primera fila fija -->
            <vs-tr :data="excelData[0]" key="header-row">
              <vs-td v-for="(cell, idx) in excelData[0]" :key="'header-' + idx">
                {{ cell | formatValue(idx) }}
              </vs-td>
            </vs-tr>

            <!-- Datos filtrados y paginados -->
            <vs-tr
              v-for="(row, index) in paginatedData"
              :key="'row-' + index"
              :data="row"
            >
              <vs-td
                v-for="(cell, idx) in row"
                :key="'cell-' + index + '-' + idx"
              >
                {{ cell | formatValue(idx) }}
              </vs-td>
            </vs-tr>
          </template>
          <template #footer>
            <vs-pagination
              v-model="page"
              :length="$vs.getLength($vs.getSearch(excelData, search), max)"
            />
          </template>
        </vs-table>
      </div>

      <not-found v-else />
    </div>
  </HXContentCard>
</template>

<script>
import * as XLSX from "xlsx";
import DropFileBasic from "@/components/helexium/DropFileBasic.vue";
const Swal = require("sweetalert2");

export default {
  components: {
    DropFileBasic,
    notFound: () => import("@/components/helexium/notFound.vue"),
  },
  data() {
    return {
      search: "",
      page: 1,
      max: 10,
      fechaInicio: null,
      fechaFin: null,
      liquidacionesDisponibles: [],
      liquidacionSeleccionada: null,
      currentHeaders: [], // Headers dinamicos de la liquidacion seleccionada
      currentFilterDate: null,
      headers: [
        "CLINICA",
        "IDENTIFICACION",
        "NOMBRE",
        "FECHA DE INGRESO",
        "TIEMPO DE PRESTACION DE SERVICIOS",
        "CATEGORIA",
        "PRIMEROS ABONOS PARA CUMPLIMIENTO",
        "META DE PRIMEROS ABONOS",
        "CUMPLIMIENTO",
        "PORCENTAJE A CANCELAR",
        "PRIMEROS ABONOS LIQUIDACION",
        "SUBVENCION Y GASTOS FINANCIEROS",
        "DEVOLUCIONES",
        "TOTAL PRIMEROS ABONOS LIQUIDACION ",
        "NOTA DEBITO CONVENIOS / ALIANZAS",
        "INCENTIVO DE PRIMEROS ABONOS SIN CUMPLIMIENTO DE META",
        "INCENTIVO DE PRIMEROS ABONOS CON CUMPLIMIENTO DE META",
        "CIERRES DE ORTODONCIA",
        "IMPLANTES MONTADOS",
        "MONTAJES DE ORTODONCIA",
        "PACIENTES QUE INICIAN",
        "HONORARIOS DE ÉXITO PACKS",
        "GARANTIZADO",
        "NOTA DEBITO CONVENIOS / ALIANZAS",
        "INCREMENTO OCACIONAL",
        "OTROS",
        "TOTAL",
        "CORTE 1a15",
        "Diferencia",
      ],
      excelData: [],
      loader: false,
      sendData: null,
      personaAct: {
        rol: {
          nivel: 0,
        },
        numeroDocumento: "",
      },
      messageFileValidation: null,
      urlPlantila:
        "https://helexiumfiles.com/documentos/PRELIQUIDACIONHONORARIOSDEEXITO.xlsx",
    };
  },
  async mounted() {
    this.personaAct = JSON.parse(localStorage.getItem("setPersonaAct"));

    await this.getHonorariosExitoTemp();
  },
  computed: {
    filteredData() {
      if (!this.search) {
        return this.excelData.slice(1); // Excluir la primera fila de la búsqueda
      }
      return this.excelData
        .slice(1)
        .filter((row) =>
          row.some((cell) =>
            String(cell).toLowerCase().includes(this.search.toLowerCase())
          )
        );
    },
    paginatedData() {
      const start = (this.page - 1) * this.max;
      const end = this.page * this.max;
      return this.filteredData.slice(start, end);
    },
    isAdmin() {
      return this.personaAct.rol.nivel >= 3;
    },
    docPersonaAct() {
      return this.personaAct.numeroDocumento;
    },
  },
  methods: {
    showToast(type, message) {
      if (type == "success") {
        this.$toast.success(message, {
          position: "top-center",
          timeout: 1492,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: false,
          draggable: false,
          draggablePercent: 0.41,
          showCloseButtonOnHover: false,
          hideProgressBar: true,
          closeButton: "button",
          icon: true,
          rtl: false,
        });
      }
      if (type == "error")
        this.$toast.error(message, {
          position: "top-center",
          timeout: 1492,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: false,
          draggable: false,
          draggablePercent: 0.41,
          showCloseButtonOnHover: false,
          hideProgressBar: true,
          closeButton: "button",
          icon: true,
          rtl: false,
        });
    },
    handleFileRemoved() {
      this.sendData = null;
      this.messageFileValidation = null;
    },
    encabezadosCombinados(header) {
      if (header == "CIERRES DE ORTODONCIA") return 9;
      if (header == "IMPLANTES MONTADOS") return 2;
      if (header == "MONTAJES DE ORTODONCIA") return 4;
      if (header == "PACIENTES QUE INICIAN") return 3;
    },
    handleFileData(event) {
      const file = event;
      const reader = new FileReader();

      console.log(file);

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        let headers = json[0].filter((item) => item !== undefined);
        let excelData = json.slice(1);
        excelData[0].push(...new Array(6).fill(undefined));

        // Aquí es donde se valida la estructura
        const isValid = this.validateExcelStructure(headers, excelData);

        if (isValid) {
          this.sendData = {
            headers,
            excelData,
          };

          console.log("sendData", this.sendData);
        } else {
          this.showToast(
            "error",
            "El archivo Excel no tiene la estructura correcta."
          );
        }
      };

      reader.readAsArrayBuffer(file);
    },
    validateExcelStructure(headers, data) {
      const expectedColumnsList = [41, 43]; // Pueden ser 41 o 43 columnas
      const expectedHeadersLengthList = [27, 29];

      this.messageFileValidation =
        "El archivo Excel no tiene la estructura correcta.";

      // Verificar que el número de encabezados sea el esperado
      if (!expectedHeadersLengthList.includes(headers.length)) {
        console.error(this.messageFileValidation);
        return false;
      }

      // Verificar que los encabezados coincidan
      for (let i = 0; i < headers.length; i++) {
        if (headers[i] !== this.headers[i]) {
          console.error(this.messageFileValidation);
          return false;
        }
      }

      // Verificar que cada fila tenga el número correcto de columnas (41 o 43)
      for (let row of data) {
        if (!expectedColumnsList.includes(row.length)) {
          console.error(this.messageFileValidation);
          return false;
        }
      }

      this.messageFileValidation = null;

      return true;
    },
    async handleFilterDateInput() {
      const [anio, mes] = this.currentFilterDate.split("-");
      await this.getHonorariosExitoTemp(anio, mes);
    },
    async getHonorariosExitoTemp(anio = null, mes = null) {
      try {
        this.$isLoading(true);

        const path =
          anio && mes
            ? `HonorariosExitoTemp/Obtener/${anio}/${mes}`
            : "HonorariosExitoTemp/Obtener";
        let res = await this.$store.dispatch("denti_get", { path });

        console.log("res", res);

        const { success, data } = res;
        if (success && data !== null) {
          // data ahora es un ARRAY de liquidaciones
          this.liquidacionesDisponibles = Array.isArray(data) ? data : [data];

          if (this.liquidacionesDisponibles.length > 0) {
            // Seleccionar la primera por defecto
            this.liquidacionSeleccionada = this.liquidacionesDisponibles[0];
            this.mostrarLiquidacion(this.liquidacionSeleccionada);
          }
        } else {
          console.log("No se encontraron datos");
          this.excelData = [];
          this.currentHeaders = [];
          this.liquidacionesDisponibles = [];
          this.liquidacionSeleccionada = null;
        }

        this.$isLoading(false);
      } catch (error) {
        console.error("Error al obtener los honorarios:", error);
        this.$isLoading(false);
        this.showToast("error", "Hubo un error al obtener los honorarios");
      }
    },
    mostrarLiquidacion(liquidacion) {
      if (!liquidacion) return;

      this.currentFilterDate = `${liquidacion.anio}-${String(
        liquidacion.mes
      ).padStart(2, "0")}`;

      // Parsear los datos de Excel
      const parsedData = JSON.parse(liquidacion.data);
      this.excelData = parsedData.excelData;
      
      // Cargar los headers de la liquidacion (dinamicos: 27 o 29 columnas)
      this.currentHeaders = parsedData.headers || this.headers;

      console.log("excelData", this.excelData);
      console.log("currentHeaders", this.currentHeaders);

      // filtrar para usuarios que no son admin
      if (!this.isAdmin) {
        this.excelData = this.excelData.filter(
          (row, idx) => row[1] == this.docPersonaAct || idx === 0
        );
      }
    },
    formatearFecha(fecha) {
      if (!fecha) return "";
      // La fecha puede venir como "2025-12-01" o "2025-12-01T00:00:00"
      const date = new Date(fecha);
      if (isNaN(date.getTime())) return "Fecha inválida";
      return date.toLocaleDateString("es-CO");
    },
    formatearFechaHora(fecha) {
      if (!fecha) return "";
      const date = new Date(fecha);
      if (isNaN(date.getTime())) return "Fecha inválida";
      return date.toLocaleDateString("es-CO");
    },
    async sendDataFile() {
      this.$isLoading(true);
      try {
        console.log("sendData", this.sendData);

        const fechaInicioDate = new Date(this.fechaInicio + "T00:00:00");
        const mes = fechaInicioDate.getMonth() + 1;
        const anio = fechaInicioDate.getFullYear();

        let data = {
          mes,
          anio,
          fechaInicio: this.fechaInicio,
          fechaFin: this.fechaFin,
          data: JSON.stringify(this.sendData),
        };

        console.log("dataSend", data);

        let res = await this.$store.dispatch("denti_post", {
          path: "HonorariosExitoTemp/GuardarData",
          data,
        });
        console.log("res", res);

        if (res.success) {
          this.$bvModal.hide("cargar-honorarios");
          this.showToast("success", res.message);
          this.fechaInicio = null;
          this.fechaFin = null;
          this.sendData = null;
          await this.getHonorariosExitoTemp(anio, mes);
        }
        this.$isLoading(false);
      } catch (error) {
        console.log("error", error);
        this.$isLoading(false);
        Swal.fire("Error!", "Error: " + error, "error");
      }
    },
  },
  filters: {
    formatValue(value, idx) {
      console.log("value", value);

      if (value === undefined || value === null) return "";

      const formatters = {
        currency: [
          6, 7, 10, 11, 12, 13, 14, 15, 16, 21, 25, 27, 31, 34, 35, 37, 40, 41, 42,
        ],
        date: [3],
        percentage: [8, 9, 20, 30],
        numberWithCommas: [4, 19, 29, 33],
      };

      if (typeof value === "number") {
        if (formatters.currency.includes(idx)) {
          // Formatear como moneda
          return new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            maximumFractionDigits: 0,
          }).format(value);
        } else if (formatters.date.includes(idx)) {
          // Formatear como fecha (en caso de que sea un valor numérico de fecha de Excel)
          const excelEpoch = new Date(1899, 11, 30); // La fecha base de Excel es el 30 de diciembre de 1899
          const date = new Date(excelEpoch.getTime() + value * 86400000); // Convertir a milisegundos
          return date.toLocaleDateString("es-CO"); // Formatear la fecha en el formato deseado
        } else if (formatters.percentage.includes(idx)) {
          // Formatear como porcentaje
          return (value * 100).toFixed(1) + "%";
        } else if (formatters.numberWithCommas.includes(idx)) {
          // Redondear a un decimal y luego formatear con separadores de miles
          return new Intl.NumberFormat("es-CO").format(
            Math.round(value * 10) / 10
          );
        }
      }

      return value;
    },
  },
};
</script>
